import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Form, message, UploadFile } from 'antd';
import { Button, Drawer, Icon, Input, Spinner } from 'ui';
import { FileUploader } from 'ui/UploadImage/FileUploader';
import { StyledFooter } from 'ui/Drawer/Drawers/style';
import useQueryApiClient from 'utils/useQueryApiClient';
import { imgSrcParser } from 'utils/imgSrcParser';
import { SocialNetwork } from 'types/SocialNetwork';
import { StyledSocialNetworkDrawer } from './style';

interface SocialNetworkDrawerProps {
  open: boolean;
  socialNetwork?: SocialNetwork;
  onClose: () => void;
  onSaved: () => void;
}

const SocialNetworkDrawer = ({
  open,
  socialNetwork,
  onClose,
  onSaved,
}: SocialNetworkDrawerProps) => {
  const intl = useIntl();
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  useEffect(() => {
    if (!open) return;

    form.setFieldsValue({ name: socialNetwork?.name });
    setFileList(
      socialNetwork?.logo
        ? [
            {
              uid: `social-network-${socialNetwork.id}`,
              name: socialNetwork.logo.split('/').pop() ?? socialNetwork.name,
              status: 'done',
              url: imgSrcParser(socialNetwork.logo),
            },
          ]
        : []
    );
  }, [open, socialNetwork, form]);

  const { appendData: save, isLoading: isSaving } = useQueryApiClient({
    request: {
      url: socialNetwork
        ? `api/v2/social-networks/${socialNetwork.id}`
        : 'api/v2/social-networks',
      method: 'POST',
      multipart: true,
    },
    onSuccess: () => {
      onSaved();
      handleClose();
    },
    onError: () =>
      message.error(intl.formatMessage({ id: 'message.error_save' })),
  });

  const handleClose = () => {
    form.resetFields();
    setFileList([]);
    onClose();
  };

  const handleFinish = (values: { name: string }) => {
    const logo = fileList[0]?.originFileObj;

    if (!socialNetwork && !(logo instanceof Blob)) {
      message.error(
        intl.formatMessage({ id: 'social_networks.logo_required' })
      );
      return;
    }

    const formData = new FormData();
    formData.append('name', values.name);

    if (logo instanceof Blob) {
      formData.append(
        'logo_image',
        new File([logo], logo.name, { type: logo.type })
      );
    }

    save(formData);
  };

  return (
    <Drawer
      open={open}
      title={intl.formatMessage({
        id: socialNetwork ? 'social_networks.edit' : 'social_networks.add',
      })}
      onClose={handleClose}
      destroyOnClose
      maskClosable
      width={'50%'}
      closeIcon={<Icon name={'global-close-icon'} type={'small'} />}
      footer={
        <StyledFooter>
          <Button
            className={'add-edit-product-button white'}
            label={intl.formatMessage({ id: 'general.cancel' })}
            onClick={handleClose}
          />
          <Button
            className={'add-edit-product-button black'}
            label={intl.formatMessage({ id: 'general.save' })}
            onClick={() => form.submit()}
            loading={isSaving}
          />
        </StyledFooter>
      }
    >
      <StyledSocialNetworkDrawer>
        <Spinner spinning={isSaving}>
          <Form form={form} layout={'vertical'} onFinish={handleFinish}>
            <Input
              name={'name'}
              label={intl.formatMessage({ id: 'general.name' })}
              validations={'required'}
            />
            <FileUploader
              onFileListChange={setFileList}
              fileList={fileList}
              multiple={false}
              title={intl.formatMessage({ id: 'general.logo' })}
              isCrop
              aspectRatio={1}
            />
          </Form>
        </Spinner>
      </StyledSocialNetworkDrawer>
    </Drawer>
  );
};

export default SocialNetworkDrawer;
